import React from 'react';
import { AdvancedMarker, Map as GoogleMap, Polyline } from '@vis.gl/react-google-maps';
import {
  googleMapsMapId,
  hasGoogleMapsApiKey,
} from '../../components/maps/GoogleMapsProvider';
import {
  FleetLocationResponse,
  FleetMapVehicle,
  OpsAuthorityResponse,
  OpsListResponse,
  getFleetLocations,
  getOpsAssignments,
  getOpsAuthority,
  getOpsDrivers,
  getVehicleLocationHistory,
} from '../../services/fleet';

type MapVehicle = {
  id: string;
  plateNumber: string;
  route: string;
  driver: string;
  speed: number;
  status: 'active' | 'idle' | 'maintenance';
  latitude: number;
  longitude: number;
  x: number;
  y: number;
};

const WS_BASE_URL = process.env.REACT_APP_WS_URL ?? 'ws://localhost:8000';
const DEFAULT_MAP_CENTER = { lat: 6.5244, lng: 3.3792 };

export function FleetMap({ accessToken = '' }: { accessToken?: string }) {
  const [vehicles, setVehicles] = React.useState<MapVehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = React.useState<MapVehicle | null>(null);
  const [history, setHistory] = React.useState<MapVehicle[]>([]);
  const [historyPoints, setHistoryPoints] = React.useState(0);
  const [isHistoryLoading, setIsHistoryLoading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [connectionStatus, setConnectionStatus] = React.useState<'live' | 'connecting' | 'offline'>('connecting');
  const [statusFilter, setStatusFilter] = React.useState<'all' | MapVehicle['status']>('all');
  const [search, setSearch] = React.useState('');
  const [zoom, setZoom] = React.useState(hasGoogleMapsApiKey ? 12 : 1);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let isActive = true;
    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    let assignments: Record<string, unknown>[] = [];
    let drivers: Record<string, unknown>[] = [];

    const connect = (fleetId: string) => {
      if (!accessToken) return;

      socket = new WebSocket(
        `${WS_BASE_URL.replace(/\/$/, '')}/location/ws/${encodeURIComponent(fleetId)}?token=${encodeURIComponent(accessToken)}`,
      );

      socket.onopen = () => {
        if (isActive) setConnectionStatus('live');
      };

      socket.onmessage = (event) => {
        const updates = parseSocketLocations(event.data);
        if (!updates.length || !isActive) return;

        setVehicles((current) => mergeVehicleUpdates(
          current,
          normalizeMapVehicles(updates, assignments, drivers),
        ));
      };

      socket.onclose = () => {
        if (isActive) {
          setConnectionStatus('connecting');
          reconnectTimer = window.setTimeout(() => connect(fleetId), 3000);
        }
      };

      socket.onerror = () => {
        if (isActive) setConnectionStatus('offline');
      };
    };

    getOpsAuthority()
      .then(async (authority) => {
        const fleetId = getFleetId(authority);
        if (!fleetId) throw new Error('No fleet found. Create a fleet group under Fleet Organization first, then return to the live map.');

        const [locationsResponse, assignmentsResponse, driversResponse] = await Promise.all([
          getFleetLocations(fleetId),
          getOpsAssignments(fleetId),
          getOpsDrivers(fleetId),
        ]);

        assignments = getOpsRecords(assignmentsResponse, 'assignments');
        drivers = getOpsRecords(driversResponse, 'drivers');

        if (isActive) {
          setVehicles(normalizeMapVehicles(locationsResponse, assignments, drivers));
          setError('');
          connect(fleetId);
        }
      })
      .catch((requestError) => {
        if (isActive) setError(requestError instanceof Error ? requestError.message : 'Unable to load live vehicles');
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [accessToken]);

  const selectVehicle = async (vehicle: MapVehicle) => {
    setSelectedVehicle(vehicle);
    setHistory([]);
    setHistoryPoints(0);

    setIsHistoryLoading(true);
    try {
      const history = await getVehicleLocationHistory(vehicle.id, 60);
      const points = normalizeMapVehicles(history);
      setHistory(points);
      setHistoryPoints(points.length);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load vehicle history');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const activeCount = vehicles.filter((vehicle) => vehicle.status === 'active').length;
  const filteredVehicles = vehicles.filter((vehicle) => {
    const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;
    const query = search.trim().toLowerCase();
    const matchesSearch = !query || [vehicle.plateNumber, vehicle.route, vehicle.driver]
      .some((value) => value.toLowerCase().includes(query));
    return matchesStatus && matchesSearch;
  });

  if (isLoading) {
    return <section className="fleet-panel empty"><h3>Loading live map</h3><p>Locating your fleet vehicles.</p></section>;
  }

  return (
    <div className="fleet-map-page">
      {error ? <div className="kyc-message passengers-error fleet-map-error" role="alert">{error}</div> : null}

      <section
        className="fleet-live-map"
        aria-label="Fleet vehicle map"
        style={{ backgroundSize: `${52 * zoom}px ${52 * zoom}px, ${52 * zoom}px ${52 * zoom}px, auto` }}
      >
        <div className="fleet-map-toolbar">
          <span className={`map-connection ${connectionStatus}`}><i />{connectionStatus}</span>
          {!hasGoogleMapsApiKey ? <div className="fleet-map-zoom">
            <button aria-label="Zoom out" disabled={zoom <= 0.8} onClick={() => setZoom((current) => Math.max(0.8, current - 0.2))} type="button">−</button>
            <button aria-label="Reset zoom" onClick={() => setZoom(1)} type="button">{Math.round(zoom * 100)}%</button>
            <button aria-label="Zoom in" disabled={zoom >= 1.8} onClick={() => setZoom((current) => Math.min(1.8, current + 0.2))} type="button">+</button>
          </div> : null}
        </div>
        {hasGoogleMapsApiKey ? (
          <GoogleMap
            center={selectedVehicle
              ? { lat: selectedVehicle.latitude, lng: selectedVehicle.longitude }
              : DEFAULT_MAP_CENTER}
            gestureHandling="greedy"
            mapId={googleMapsMapId}
            mapTypeControl={false}
            onCameraChanged={(event) => setZoom(event.detail.zoom)}
            streetViewControl={false}
            zoom={Math.max(3, Math.min(20, zoom))}
          >
            {history.length > 1 ? (
              <Polyline
                path={history.map((point) => ({ lat: point.latitude, lng: point.longitude }))}
                strokeColor="#126a53"
                strokeOpacity={0.8}
                strokeWeight={4}
              />
            ) : null}
            {history.map((point, index) => (
              <AdvancedMarker
                key={`${point.id}-${index}`}
                position={{ lat: point.latitude, lng: point.longitude }}
              >
                <span className="google-history-point" />
              </AdvancedMarker>
            ))}
            {filteredVehicles.map((vehicle) => (
              <AdvancedMarker
                key={vehicle.id}
                onClick={() => selectVehicle(vehicle)}
                position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
                title={`${vehicle.plateNumber} - ${vehicle.driver} - ${vehicle.speed} kph`}
              >
                <button
                  aria-label={`${vehicle.plateNumber}, ${vehicle.status}`}
                  className={`google-fleet-marker ${vehicle.status}${selectedVehicle?.id === vehicle.id ? ' selected' : ''}`}
                  type="button"
                >
                  <TruckIcon />
                </button>
              </AdvancedMarker>
            ))}
          </GoogleMap>
        ) : (
          <>
            <span className="fleet-map-road fleet-map-road-one" />
            <span className="fleet-map-road fleet-map-road-two" />
            <span className="fleet-map-road fleet-map-road-three" />
            <span className="fleet-map-place place-victoria">Victoria Island</span>
            <span className="fleet-map-place place-lekki">Lekki</span>
            <span className="fleet-map-place place-ikoyi">Ikoyi</span>

            {history.map((point, index) => (
              <span
                aria-hidden="true"
                className="fleet-history-point"
                key={`${point.id}-${index}`}
                style={{ left: `${point.x}%`, top: `${point.y}%` }}
              />
            ))}

            {filteredVehicles.map((vehicle) => (
              <button
                aria-label={`${vehicle.plateNumber}, ${vehicle.status}`}
                className={`fleet-map-marker ${vehicle.status}${selectedVehicle?.id === vehicle.id ? ' selected' : ''}`}
                key={vehicle.id}
                onClick={() => selectVehicle(vehicle)}
                style={{ left: `${vehicle.x}%`, top: `${vehicle.y}%` }}
                title={`${vehicle.plateNumber} - ${vehicle.driver} - ${vehicle.speed} kph`}
                type="button"
              >
                <span />
              </button>
            ))}
          </>
        )}

        {selectedVehicle ? (
          <article className="fleet-map-selection">
            <button aria-label="Close selected vehicle" onClick={() => { setSelectedVehicle(null); setHistory([]); }} type="button">×</button>
            <span className={`pill ${statusTone(selectedVehicle.status)}`}>{selectedVehicle.status}</span>
            <strong>{selectedVehicle.plateNumber}</strong>
            <span>{selectedVehicle.route} · {selectedVehicle.driver}</span>
            <em>{selectedVehicle.speed} kph</em>
          </article>
        ) : null}

        <div className="fleet-map-legend" aria-label="Map legend">
          <span><i className="active" />Active</span>
          <span><i className="idle" />Idle</span>
          <span><i className="maintenance" />Maintenance</span>
        </div>
      </section>

      <aside className="fleet-panel live-vehicles-panel">
        <div className="fleet-panel-head">
          <h2>Live vehicles</h2>
          <span className="pill success">{activeCount} active</span>
        </div>
        <label className="live-vehicle-search">
          <SearchIcon />
          <input aria-label="Search live vehicles" onChange={(event) => setSearch(event.target.value)} placeholder="Search vehicle, route, driver..." type="search" value={search} />
        </label>
        <div className="live-status-filters" aria-label="Filter vehicles by status">
          {(['all', 'active', 'idle', 'maintenance'] as const).map((status) => (
            <button className={statusFilter === status ? 'active' : ''} key={status} onClick={() => setStatusFilter(status)} type="button">
              {status}
            </button>
          ))}
        </div>
        <div className="live-vehicle-list">
          {filteredVehicles.length ? filteredVehicles.map((vehicle) => (
            <button className={`live-vehicle-card${selectedVehicle?.id === vehicle.id ? ' selected' : ''}`} key={vehicle.id} onClick={() => selectVehicle(vehicle)} type="button">
              <span className="fleet-icon green"><TruckIcon /></span>
              <span className="live-vehicle-info">
                <strong>{vehicle.plateNumber} - {vehicle.route}</strong>
                <span>{vehicle.driver} - {vehicle.speed} kph</span>
              </span>
              <span className={`pill ${statusTone(vehicle.status)}`}>{vehicle.status}</span>
            </button>
          )) : (
            <div className="empty"><h3>No matching vehicles</h3><p>Adjust the search or status filter.</p></div>
          )}
        </div>

        {selectedVehicle ? (
          <section className="vehicle-history-summary" aria-live="polite">
            <div>
              <strong>{selectedVehicle.plateNumber}</strong>
              <span>Last 60 minutes</span>
            </div>
            <span>{isHistoryLoading ? 'Loading...' : `${historyPoints} location points`}</span>
          </section>
        ) : null}
      </aside>
    </div>
  );
}

function normalizeMapVehicles(
  response: FleetLocationResponse | FleetMapVehicle[],
  assignments: Record<string, unknown>[] = [],
  drivers: Record<string, unknown>[] = [],
): MapVehicle[] {
  return getLocationSource(response).map((vehicle, index) => {
    const position = vehicle.position ?? {};
    const id = vehicle.id ?? vehicle.vehicle_id ?? String(index);
    const assignment = assignments.find((item) => readString(item, 'vehicle_id') === id);
    const driverId = assignment ? readString(assignment, 'driver_id') : undefined;
    const driver = drivers.find((item) => readString(item, 'id', 'driver_id', 'user_id') === driverId);
    const latitude = vehicle.latitude ?? vehicle.lat ?? position.latitude ?? position.lat;
    const longitude = vehicle.longitude ?? vehicle.lng ?? position.longitude ?? position.lng;
    const fallback = fallbackCoordinate(index);

    return {
      id,
      plateNumber: vehicle.registration_number ?? vehicle.plateNumber ?? vehicle.plate_number ?? vehicle.name ?? readString(assignment, 'vehicle_registration_number', 'vehicle_plate_number') ?? 'Unknown vehicle',
      route: vehicle.route ?? vehicle.route_name ?? vehicle.route_code ?? readString(assignment, 'route_code', 'route_name') ?? 'No route',
      driver: vehicle.driver ?? vehicle.driver_name ?? readString(assignment, 'driver_name') ?? readString(driver, 'full_name', 'name') ?? '-',
      speed: vehicle.speed ?? vehicle.speed_kph ?? 0,
      status: normalizeStatus(vehicle.status),
      latitude: typeof latitude === 'number' ? latitude : fallback.latitude,
      longitude: typeof longitude === 'number' ? longitude : fallback.longitude,
      x: clampPercent(vehicle.x ?? position.x ?? longitudeToPercent(longitude) ?? fallbackPosition(index, 17)),
      y: clampPercent(vehicle.y ?? position.y ?? latitudeToPercent(latitude) ?? fallbackPosition(index, 23)),
    };
  });
}

function getLocationSource(response: FleetLocationResponse | FleetMapVehicle[]): FleetMapVehicle[] {
  if (Array.isArray(response)) return response;
  const direct = response.vehicles ?? response.locations ?? response.history ?? response.liveVehicles ?? response.live_vehicles ?? response.items ?? response.results;
  if (direct) return direct;
  return response.data ? getLocationSource(response.data) : [];
}

function getFleetId(authority: OpsAuthorityResponse): string | undefined {
  // Fast path — direct scalar fields (older/alternate API shapes)
  const direct = authority.fleet_id
    ?? authority.fleetId
    ?? authority.fleet?.id
    ?? authority.fleet?.fleet_id;
  if (direct) return direct;

  // Primary path — walk the nested authority tree returned by GET /ops/authority
  // Shape: { authority: [{ id, group_type, children: [...] }] }
  const nodes: Record<string, unknown>[] = Array.isArray(authority.authority)
    ? (authority.authority as Record<string, unknown>[])
    : authority.fleets ?? authority.groups ?? authority.items ?? authority.results ?? [];

  const found = findFleetNode(nodes);
  if (found) return found;

  // Fallback — recurse into data wrapper
  return authority.data ? getFleetId(authority.data) : undefined;
}

function findFleetNode(nodes: Record<string, unknown>[]): string | undefined {
  for (const node of nodes) {
    const groupType = typeof node.group_type === 'string' ? node.group_type : typeof node.type === 'string' ? node.type : '';
    const id = typeof node.id === 'string' ? node.id : typeof node.fleet_id === 'string' ? node.fleet_id : undefined;
    // Prefer a "fleet" typed node; treat untyped root nodes as fleet too
    if (id && (groupType === 'fleet' || groupType === '' || !groupType)) return id;
    // Recurse into children
    if (Array.isArray(node.children)) {
      const childResult = findFleetNode(node.children as Record<string, unknown>[]);
      if (childResult) return childResult;
    }
  }
  // Second pass — accept authority group IDs if nothing else found
  for (const node of nodes) {
    const id = typeof node.id === 'string' ? node.id : typeof node.fleet_id === 'string' ? node.fleet_id : undefined;
    if (id) return id;
    if (Array.isArray(node.children)) {
      const childResult = findFleetNode(node.children as Record<string, unknown>[]);
      if (childResult) return childResult;
    }
  }
  return undefined;
}

function getOpsRecords(response: OpsListResponse<Record<string, unknown>>, key: 'assignments' | 'drivers'): Record<string, unknown>[] {
  if (Array.isArray(response)) return response;
  const direct = response[key] ?? response.items ?? response.results;
  if (direct) return direct;
  return response.data ? getOpsRecords(response.data, key) : [];
}

function parseSocketLocations(data: unknown): FleetMapVehicle[] {
  try {
    const payload = typeof data === 'string' ? JSON.parse(data) : data;
    if (Array.isArray(payload)) return payload;
    if (!isObject(payload)) return [];
    const nested = getLocationSource(payload as FleetLocationResponse);
    return nested.length ? nested : [payload as FleetMapVehicle];
  } catch {
    return [];
  }
}

function mergeVehicleUpdates(current: MapVehicle[], updates: MapVehicle[]) {
  const merged = new Map(current.map((vehicle) => [vehicle.id, vehicle]));
  updates.forEach((vehicle) => merged.set(vehicle.id, { ...merged.get(vehicle.id), ...vehicle }));
  return Array.from(merged.values());
}

function normalizeStatus(status: string | undefined): MapVehicle['status'] {
  const normalized = status?.toLowerCase();
  if (normalized === 'maintenance' || normalized === 'inactive' || normalized === 'out_of_service') return 'maintenance';
  if (normalized === 'idle' || normalized === 'offline' || normalized === 'parked') return 'idle';
  return 'active';
}

function longitudeToPercent(longitude: number | undefined) {
  return typeof longitude === 'number' ? ((longitude + 180) / 360) * 100 : undefined;
}

function latitudeToPercent(latitude: number | undefined) {
  return typeof latitude === 'number' ? ((90 - latitude) / 180) * 100 : undefined;
}

function fallbackPosition(index: number, offset: number) {
  return 18 + ((index * offset) % 64);
}

function fallbackCoordinate(index: number) {
  return {
    latitude: DEFAULT_MAP_CENTER.lat + (((index % 5) - 2) * 0.012),
    longitude: DEFAULT_MAP_CENTER.lng + (((index % 7) - 3) * 0.014),
  };
}

function clampPercent(value: number) {
  return Math.min(92, Math.max(8, value));
}

function statusTone(status: MapVehicle['status']) {
  if (status === 'active') return 'success';
  if (status === 'maintenance') return 'danger';
  return 'muted';
}

function readString(source: Record<string, unknown> | undefined, ...keys: string[]) {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function TruckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h12v10H3zM15 10h4l2 3v3h-6z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M16 16l4 4" />
    </svg>
  );
}
